import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { flashcardsData } from "@/data/mockData";
import { streakService } from "./streakService";
import { buildFlashcardDuplicateKey, getValidUuidOrNull, isUniqueConstraintError, toNullableUuid, handleSupabaseError } from "@/utils/duplicateUtils";
import { profileService } from "./profileService";
import { activityService } from "./activityService";
import { updateCardSRS, estimateExistingInterval } from "@/utils/srsAlgorithm";

import { frenchVerbFlashcards } from "@/data/frenchVerbFlashcards";
import { frenchConnectorFlashcards } from "@/data/frenchConnectorFlashcards";
import { frenchPrepositionFlashcards } from "@/data/frenchPrepositionFlashcards";

export interface FlashcardRecord {
  id: string;
  front: string;
  back: string;
  category?: string;
  source?: "writing_ai_correction" | "word_bank" | "error_bank" | "static_deck" | string;
  sourceCorrectionId?: string; // maps to source_test_id
  sourceErrorId?: string;      // maps to source_error_id
  sourceWordId?: string;       // maps to source_word_id
  difficulty: "easy" | "medium" | "hard" | "difficult" | string;
  status?: "new" | string;
  createdAt?: string;
  updatedAt?: string;
  nextReviewAt?: string;       // maps to next_review_at
  lastReviewedAt?: string;     // maps to last_reviewed_at
  reviewCount?: number;        // maps to repetitions
  type?: string;
  language?: string;
  tags?: any;
  metadata?: any;
}

const STORAGE_KEY = "tef_custom_flashcards";

import {
  normalizeFlashcardDifficulty,
  getFlashcardDifficultyLabel,
  getFlashcardDifficultyBadgeClass,
} from "@/utils/flashcardDifficulty";
import type { FlashcardDifficulty } from "@/utils/flashcardDifficulty";

export { normalizeFlashcardDifficulty, getFlashcardDifficultyLabel, getFlashcardDifficultyBadgeClass };

export function isWritingV2Flashcard(card: any): boolean {
  if (!card) return false;
  const isV2 = 
    card.source === "ee_v2_pedagogical_correction" ||
    card.metadata?.writingVersion === 2 ||
    card.metadata?.createdFrom === "writing_v2_error" ||
    (card.type === "error" && card.metadata?.writingVersion === 2);
  return Boolean(isV2);
}


export function mapSupabaseToFlashcard(row: any): FlashcardRecord {
  const meta = row.metadata || {};
  const difficulty = normalizeFlashcardDifficulty(
    meta.difficulty ?? 
    meta.initialDifficulty ?? 
    row.difficulty ?? 
    row.estimated_difficulty ?? 
    row.initial_difficulty ?? 
    "medium"
  );

  return {
    id: row.id,
    front: row.front || "",
    back: row.back || "",
    category: row.category || "Geral",
    source: row.source || undefined,
    sourceCorrectionId: row.source_test_id || undefined,
    sourceErrorId: row.source_error_id || undefined,
    sourceWordId: row.source_word_id || undefined,
    difficulty,
    status: meta.status || "new",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    nextReviewAt: row.next_review_at,
    lastReviewedAt: row.last_reviewed_at,
    reviewCount: row.repetitions || 0,
    type: row.type || "text",
    language: row.language || "fr",
    tags: row.tags || [],
    metadata: meta
  };
}

export function mapFlashcardToSupabase(card: any): any {
  const dupKey = buildFlashcardDuplicateKey(card);

  let sanitizedTags: string[] = [];
  if (typeof card.tags === "string") {
    sanitizedTags = (card.tags as string).split(",").map(t => t.trim()).filter(Boolean);
  } else if (Array.isArray(card.tags)) {
    sanitizedTags = card.tags.map((t: any) => String(t).trim()).filter(Boolean);
  }

  let sanitizedMetadata: any = {};
  if (typeof card.metadata === "string") {
    try {
      sanitizedMetadata = JSON.parse(card.metadata);
    } catch {
      sanitizedMetadata = { note: card.metadata };
    }
  } else if (card.metadata && typeof card.metadata === "object") {
    sanitizedMetadata = { ...card.metadata };
  }

  const normDifficulty = normalizeFlashcardDifficulty(
    card.difficulty ?? 
    card.estimatedDifficulty ?? 
    card.estimated_difficulty ?? 
    sanitizedMetadata.difficulty ?? 
    sanitizedMetadata.initialDifficulty ?? 
    "medium"
  );

  sanitizedMetadata.difficulty = normDifficulty;
  sanitizedMetadata.initialDifficulty = normDifficulty;
  sanitizedMetadata.status = card.status || sanitizedMetadata.status || "new";

  return {
    front: (card.front || "").trim(),
    back: (card.back || "").trim(),
    type: card.type || "text",
    language: card.language || "fr",
    category: card.category || "Geral",
    // Removendo campos que podem não existir na tabela para evitar erros de insert
    // difficulty: normDifficulty,
    // estimated_difficulty: normDifficulty, 
    source: card.source || "manual",
    source_error_id: toNullableUuid(card.sourceErrorId ?? card.source_error_id),
    source_word_id: toNullableUuid(card.sourceWordId ?? card.source_word_id),
    source_question_id: toNullableUuid(card.type === "question" ? card.id : null),
    source_test_id: toNullableUuid(card.sourceCorrectionId ?? card.source_test_id),
    ease: Number.isFinite(Number(card.ease)) ? Number(card.ease) : 250,
    repetitions: Number.isFinite(Number(card.reviewCount ?? card.repetitions)) ? Number(card.reviewCount ?? card.repetitions) : 0,
    next_review_at: card.nextReviewAt || new Date().toISOString(),
    tags: sanitizedTags,
    metadata: sanitizedMetadata,
    duplicate_key: dupKey
  };
}

export const flashcardService = {
  // Broadcaster
  emitUpdates() {
    window.dispatchEvent(new Event("flashcardsUpdated"));
    window.dispatchEvent(new Event("dashboardUpdated"));
  },

  // Importar / Materializar Decks Estáticos no Supabase
  async ensureStaticDeckImported(deckType: "verbs" | "connectors" | "prepositions" | "all"): Promise<void> {
    if (!isSupabaseConfigured || !supabase) return;
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) return;

      if (deckType === "all") {
        await this.ensureStaticDeckImported("verbs");
        await this.ensureStaticDeckImported("connectors");
        await this.ensureStaticDeckImported("prepositions");
        return;
      }

      let staticCards: any[] = [];
      if (deckType === "verbs") staticCards = frenchVerbFlashcards;
      else if (deckType === "connectors") staticCards = frenchConnectorFlashcards;
      else if (deckType === "prepositions") staticCards = frenchPrepositionFlashcards;

      // Buscar os existentes do usuário para esse deck
      const { data: existingData, error: fetchErr } = await supabase
        .from("flashcards")
        .select("duplicate_key")
        .eq("user_id", userData.user.id)
        .not("duplicate_key", "is", null);

      const existingKeys = new Set((existingData || []).map(r => r.duplicate_key));

      const toInsert: any[] = [];
      for (const card of staticCards) {
        let dupKey = "";
        if (deckType === "verbs") dupKey = `static-verb-${card.id}-${card.verb}-${card.tense}`;
        else if (deckType === "connectors") dupKey = `static-connector-${card.id}-${card.front}`;
        else if (deckType === "prepositions") dupKey = `static-preposition-${card.id}-${card.preposition}`;
        
        dupKey = dupKey.toLowerCase().replace(/[^a-z0-9_-]/g, "-");

        if (existingKeys.has(dupKey)) continue;

        let payloadMetadata: any = {};
        if (deckType === "verbs") {
          payloadMetadata = {
            staticDeck: "verbs",
            staticId: card.id,
            verb: card.verb,
            tense: card.tense,
            prompts: card.prompts,
            answers: card.answers,
            example: card.example
          };
        } else if (deckType === "connectors") {
          payloadMetadata = {
            staticDeck: "connectors",
            staticId: card.id,
            connector: card.front,
            example: card.example
          };
        } else if (deckType === "prepositions") {
          payloadMetadata = {
            staticDeck: "prepositions",
            staticId: card.id,
            preposition: card.preposition,
            example: card.example
          };
        }

        toInsert.push({
          user_id: userData.user.id,
          front: (card.front || "").trim(),
          back: (card.back || "").trim(),
          type: deckType === "verbs" ? "verb" : deckType === "connectors" ? "connector" : "preposition",
          language: "fr",
          category: card.category || "Geral",
          source: "static_deck",
          ease: 250,
          repetitions: 0,
          next_review_at: new Date().toISOString(),
          tags: Array.isArray(card.tags) ? card.tags : [],
          metadata: payloadMetadata,
          duplicate_key: dupKey
        });
      }

      if (toInsert.length > 0) {
        console.log(`[FlashcardService] Materializando ${toInsert.length} cards estáticos para o deck ${deckType}`);
        const { error: insErr } = await supabase.from("flashcards").insert(toInsert);
        if (insErr) {
          console.error("[FlashcardService] Erro ao materializar deck:", insErr);
        } else {
          this.emitUpdates();
        }
      }
    } catch (err) {
      console.error("[FlashcardService] ensureStaticDeckImported error", err);
    }
  },

  // Get all flashcards
  async getFlashcards(filters?: { studyType?: string; type?: string }): Promise<FlashcardRecord[]> {
    let list: FlashcardRecord[] = [];
    if (!isSupabaseConfigured || !supabase) {
      list = this.getLocalFlashcards();
    } else {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          return [];
        }
        const { data, error } = await supabase
          .from("flashcards")
          .select("*")
          .eq("user_id", userData.user.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        list = (data || []).map(mapSupabaseToFlashcard);
      } catch (err: any) {
        throw handleSupabaseError(err, "flashcard");
      }
    }

    const studyType = filters?.studyType || filters?.type || "words";

    if (studyType !== "all") {
      list = list.filter(c => {
        if (studyType === "words") {
          if (c.type === "verb" || c.type === "connector" || c.type === "preposition") return false;
          if (c.metadata?.staticDeck) return false;
          if (isWritingV2Flashcard(c)) return false;
          return true;
        } else if (studyType === "verbs") {
          return c.type === "verb" || c.metadata?.staticDeck === "verbs";
        } else if (studyType === "connectors") {
          return c.type === "connector" || c.metadata?.staticDeck === "connectors";
        } else if (studyType === "prepositions") {
          return c.type === "preposition" || c.metadata?.staticDeck === "prepositions";
        } else if (studyType === "writing_v2") {
          return isWritingV2Flashcard(c);
        }
        return true;
      });
    }

    return list;
  },

  // Get single flashcard
  async getFlashcardById(id: string): Promise<FlashcardRecord | null> {
    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalFlashcards();
      return local.find(c => c.id === id) || null;
    }
    try {
      if (id.startsWith("f") || id.startsWith("fc_") || id.startsWith("fc_vocab_") || !getValidUuidOrNull(id)) {
        const local = this.getLocalFlashcards();
        return local.find(c => c.id === id) || null;
      }
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return null;
      }
      const { data, error } = await supabase
        .from("flashcards")
        .select("*")
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .single();

      if (error) throw error;
      return data ? mapSupabaseToFlashcard(data) : null;
    } catch (err: any) {
      throw handleSupabaseError(err, "flashcard");
    }
  },

  // Create flashcard
  async createFlashcard(card: Omit<FlashcardRecord, "id"> & { id?: string }): Promise<FlashcardRecord> {
    streakService.registerActivity();
    const dupKey = buildFlashcardDuplicateKey(card);

    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalFlashcards();
      const isDup = local.some(c => buildFlashcardDuplicateKey(c) === dupKey);
      if (isDup) {
        throw new Error("Este flashcard já existe.");
      }
      if (local.length >= 50) {
        throw new Error("TRIAL_LIMIT_EXCEEDED: flashcards");
      }
      return this.createLocalFlashcard(card);
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível salvar flashcard.");
      }

      console.log("[FlashcardService] creating flashcard", card);

      if (!card.front || !card.front.trim()) {
        throw new Error("A frente do flashcard é obrigatória.");
      }
      if (!card.back || !card.back.trim()) {
        throw new Error("O verso do flashcard é obrigatório.");
      }

      const { data: existing, error: findError } = await supabase
        .from("flashcards")
        .select("id")
        .eq("duplicate_key", dupKey)
        .eq("user_id", userData.user.id)
        .maybeSingle();

      if (existing) {
        throw new Error("Este flashcard já existe.");
      }

      // Check limits
      const profile = await profileService.fetchOrCreateProfile(userData.user.id, userData.user.email || "");
      if (profile && (profile.plan === "free" || profile.plan === "trial" || profile.plan === "expired")) {
        const { count, error: countError } = await supabase
          .from("flashcards")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userData.user.id);
        
        if (countError) throw countError;
        
        if ((count || 0) >= 50) {
          throw new Error("TRIAL_LIMIT_EXCEEDED: flashcards");
        }
      }

      const row = {
        ...mapFlashcardToSupabase(card),
        user_id: userData.user.id
      };
      
      console.log("[flashcardService.createFlashcard] normalizedDifficulty:", row.difficulty);
      console.log("[flashcardService.createFlashcard] insertPayload:", row);

      const { data, error } = await supabase
        .from("flashcards")
        .insert([row])
        .select()
        .single();

      if (error) {
        console.error("[flashcardService.createFlashcard] Supabase error:", error);
        throw error;
      }
      console.log("[flashcardService.createFlashcard] saved row:", data);

      // Track activity log
      try {
        await activityService.trackUserActivity(
          "flashcard_created",
          "flashcard",
          data?.id || "",
          { category: card.category || "General", front: card.front.substring(0, 50) }
        );
      } catch (err) {
        console.warn("Failed to track activity:", err);
      }

      this.emitUpdates();
      return mapSupabaseToFlashcard(data);
    } catch (err: any) {
      if (err.message === "Este flashcard já existe." || err.message === "A frente do flashcard é obrigatória." || err.message === "O verso do flashcard é obrigatório." || err.message?.includes("Usuário não autenticado") || err.message === "TRIAL_LIMIT_EXCEEDED: flashcards") {
        throw err;
      }
      throw handleSupabaseError(err, "flashcard");
    }
  },

  // Update flashcard
  async updateFlashcard(id: string, card: Partial<FlashcardRecord>): Promise<FlashcardRecord> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar flashcard.");
    }

    const current = await this.getFlashcardById(id);
    if (!current) throw new Error("Flashcard not found");

    const merged = { ...current, ...card };
    const dupKey = buildFlashcardDuplicateKey(merged);

    if (!isSupabaseConfigured || !supabase || id.startsWith("f") || id.startsWith("fc_") || id.startsWith("fc_vocab_") || !getValidUuidOrNull(id)) {
      const local = this.getLocalFlashcards();
      const isDup = local.some(c => c.id !== id && buildFlashcardDuplicateKey(c) === dupKey);
      if (isDup) {
        throw new Error("Este flashcard já existe.");
      }
      return this.updateLocalFlashcard(id, card);
    }

    try {
      if (!merged.front || !merged.front.trim()) {
        throw new Error("A frente do flashcard é obrigatória.");
      }
      if (!merged.back || !merged.back.trim()) {
        throw new Error("O verso do flashcard é obrigatório.");
      }

      const { data: existing, error: findError } = await supabase
        .from("flashcards")
        .select("id")
        .eq("duplicate_key", dupKey)
        .eq("user_id", userData.user.id)
        .neq("id", id)
        .maybeSingle();

      if (existing) {
        throw new Error("Este flashcard já existe.");
      }

      const row = {
        ...mapFlashcardToSupabase(merged),
        user_id: userData.user.id
      };

      console.log("[flashcardService.updateFlashcard] normalizedDifficulty:", row.difficulty);
      console.log("[flashcardService.updateFlashcard] updatePayload:", row);

      const { data, error } = await supabase
        .from("flashcards")
        .update(row)
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .select()
        .single();

      if (error) {
        console.error("[flashcardService.updateFlashcard] Supabase error:", error);
        throw error;
      }
      console.log("[flashcardService.updateFlashcard] saved row:", data);
      this.emitUpdates();
      return mapSupabaseToFlashcard(data);
    } catch (err: any) {
      if (err.message === "Este flashcard já existe." || err.message === "A frente do flashcard é obrigatória." || err.message === "O verso do flashcard é obrigatório.") {
        throw err;
      }
      throw handleSupabaseError(err, "flashcard");
    }
  },

  // Delete flashcard
  async deleteFlashcard(id: string): Promise<void> {
    if (!isSupabaseConfigured || !supabase || id.startsWith("f") || id.startsWith("fc_") || id.startsWith("fc_vocab_") || !getValidUuidOrNull(id)) {
      return this.deleteLocalFlashcard(id);
    }
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível salvar flashcard.");
      }

      const { error } = await supabase
        .from("flashcards")
        .delete()
        .eq("id", id)
        .eq("user_id", userData.user.id);

      if (error) throw error;
      this.emitUpdates();
    } catch (err: any) {
      throw handleSupabaseError(err, "flashcard");
    }
  },

  // Delete multiple flashcards
  async deleteMultipleFlashcards(ids: string[]): Promise<number> {
    if (!ids || ids.length === 0) return 0;

    // Split local vs database IDs
    const dbIds = ids.filter(id => !id.startsWith("f") && !id.startsWith("fc_") && getValidUuidOrNull(id));
    const localIds = ids.filter(id => id.startsWith("f") || id.startsWith("fc_") || !getValidUuidOrNull(id));

    // Handle local deletes
    if (localIds.length > 0) {
      const list = this.getLocalFlashcards();
      const filtered = list.filter(c => !localIds.includes(c.id));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    }

    // Handle database deletes
    if (isSupabaseConfigured && supabase && dbIds.length > 0) {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData?.user) {
          throw new Error("Usuário não autenticado. Não é possível excluir flashcards.");
        }

        const { error } = await supabase
          .from("flashcards")
          .delete()
          .in("id", dbIds)
          .eq("user_id", userData.user.id);

        if (error) throw error;
      } catch (err: any) {
        throw handleSupabaseError(err, "flashcard");
      }
    }

    this.emitUpdates();
    return ids.length;
  },

  // Delete all flashcards for current user
  async deleteAllFlashcards(): Promise<void> {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));

    if (isSupabaseConfigured && supabase) {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData?.user) {
          throw new Error("Usuário não autenticado. Não é possível excluir flashcards.");
        }

        const { error } = await supabase
          .from("flashcards")
          .delete()
          .eq("user_id", userData.user.id);

        if (error) throw error;
      } catch (err: any) {
        console.error("[FlashcardService] deleteAllFlashcards error:", err);
        throw handleSupabaseError(err, "flashcard");
      }
    }

    this.emitUpdates();
  },

  // --- WRAPPERS FOR OLD / COMPATIBILITY INTEGRATIONS ---
  saveFlashcardsList(cards: FlashcardRecord[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
    this.emitUpdates();
  },

  async createFlashcardsFromWritingErrors({
    correctionId,
    errors,
    section,
    competency
  }: {
    correctionId: string;
    errors: any[];
    section?: string;
    competency?: string;
  }): Promise<{ createdCount: number; skippedCount: number; createdFlashcards: FlashcardRecord[] }> {
    streakService.registerActivity();
    const createdFlashcards: FlashcardRecord[] = [];
    let createdCount = 0;
    let skippedCount = 0;

    for (const err of errors) {
      const front = err.original || err.original_sentence || "";
      const back = `${err.corrected || err.correction || err.corrected_sentence || ""}\n\n${err.explanation || ""}`;
      const category = err.category || "correction_ia";

      let difficulty: "easy" | "medium" | "difficult" = "medium";
      const severity = (err.severity || "").toLowerCase();
      if (severity === "high" || severity === "grave") {
        difficulty = "difficult";
      } else if (severity === "low" || severity === "leve") {
        difficulty = "easy";
      } else if (severity === "medium" || severity === "médio" || severity === "medio") {
        difficulty = "medium";
      }

      const cardPayload: any = {
        front,
        back,
        category,
        source: "ai_correction",
        sourceCorrectionId: correctionId,
        sourceErrorId: err.id || undefined,
        difficulty,
        status: "new",
        nextReviewAt: new Date().toISOString(),
        reviewCount: 0,
        type: "error",
        language: "fr",
        metadata: {
          correctionSource: "gemini",
          originalSentence: err.original || err.original_sentence || "",
          correctedSentence: err.corrected || err.correction || err.corrected_sentence || "",
          explanation: err.explanation || "",
          testAttemptId: correctionId
        }
      };

      try {
        const created = await this.createFlashcard(cardPayload);
        createdFlashcards.push(created);
        createdCount++;
      } catch (e: any) {
        if (e.message && e.message.includes("já existe")) {
          skippedCount++;
        } else {
          console.error("[FlashcardService] Error creating flashcard in loop:", e);
        }
      }
    }

    return { createdCount, skippedCount, createdFlashcards };
  },

  async createFlashcardFromWritingV2Error({
    error,
    task,
    historyRecordId,
  }: {
    error: any;
    task?: any | null;
    historyRecordId?: string | null;
  }) {
    console.log("[EE_V2_CREATE_FLASHCARD_PAYLOAD_PRE]", { error, task, historyRecordId });

    let difficulty: "easy" | "medium" | "difficult" = "medium";
    const severity = (error.severity || "").toLowerCase();
    if (severity === "high") difficulty = "difficult";
    else if (severity === "low") difficulty = "easy";

    const front = error.flashcardSuggestion?.front || `Corrija a frase em francês:\n"${error.original}"`;
    
    const parts = [];
    const suggestedBack = error.flashcardSuggestion?.back || "";
    
    if (suggestedBack) {
      parts.push(suggestedBack);
      
      // Enriquecer com explicação se não estiver inclusa
      if (error.explanation && !suggestedBack.toLowerCase().includes(error.explanation.toLowerCase())) {
        parts.push(`Explicação:\n${error.explanation}`);
      }
    } else {
      if (error.corrected) {
        parts.push(`Correção:\n${error.corrected}`);
      }
      if (error.explanation) {
        parts.push(`Explicação:\n${error.explanation}`);
      }
    }

    if (error.contextSentence) {
      parts.push(`Contexto:\n${error.contextSentence}`);
    }

    if (error.category || error.severity) {
      parts.push(`Categoria:\n${error.category || "Outro"}${error.severity ? ` · ${error.severity}` : ""}`);
    }

    const back = parts.join("\n\n");

    const cardPayload: Omit<FlashcardRecord, "id"> = {
      front,
      back,
      category: error.category || "Grammaire",
      type: "error",
      language: "fr",
      difficulty,
      source: "ee_v2_pedagogical_correction",
      sourceErrorId: error.id,
      sourceCorrectionId: historyRecordId || undefined,
      metadata: {
        writingVersion: 2,
        sourceErrorId: error.id,
        section: error.section,
        original: error.original,
        corrected: error.corrected,
        contextSentence: error.contextSentence,
        explanation: error.explanation,
        taskId: task?.id || null,
        taskTitle: task?.title || "",
        historyRecordId: historyRecordId || null,
        createdFrom: "writing_v2_error",
      }
    };

    return this.createFlashcard(cardPayload);
  },

  async createFlashcardsFromWritingV2Errors({
    errors,
    task,
    historyRecordId,
  }: {
    errors: any[];
    task?: any | null;
    historyRecordId?: string | null;
  }) {
    const createdFlashcards: FlashcardRecord[] = [];
    const results = { createdCount: 0, skippedCount: 0, failedCount: 0 };

    for (const error of errors) {
      try {
        const created = await this.createFlashcardFromWritingV2Error({ error, task, historyRecordId });
        createdFlashcards.push(created);
        results.createdCount++;
      } catch (e: any) {
        if (e.message && e.message.includes("já existe")) {
          results.skippedCount++;
        } else {
          console.error("[FlashcardService] Error creating flashcard in loop:", e);
          results.failedCount++;
        }
      }
    }

    return { ...results, createdFlashcards };
  },

  // --- SPACED REPETITION SYSTEM (SRS) ---
  async getDueFlashcards(filters?: { studyType?: string; type?: string }): Promise<FlashcardRecord[]> {
    let list: FlashcardRecord[] = [];
    if (!isSupabaseConfigured || !supabase) {
      const all = this.getLocalFlashcards();
      const now = new Date();
      list = all.filter(c => !c.nextReviewAt || new Date(c.nextReviewAt) <= now)
                .sort((a, b) => new Date(a.nextReviewAt || 0).getTime() - new Date(b.nextReviewAt || 0).getTime());
    } else {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (userData?.user) {
          const now = new Date().toISOString();
          const { data, error } = await supabase
            .from("flashcards")
            .select("*")
            .eq("user_id", userData.user.id)
            .or(`next_review_at.is.null,next_review_at.lte.${now}`)
            .order("next_review_at", { ascending: true, nullsFirst: true })
            .order("created_at", { ascending: true });

          if (error) throw error;
          list = (data || []).map(mapSupabaseToFlashcard);
        }
      } catch (err: any) {
        console.error("[FlashcardService] getDueFlashcards error", err);
      }
    }

    const studyType = filters?.studyType || filters?.type || "words";

    if (studyType !== "all") {
      list = list.filter(c => {
        if (studyType === "words") {
          if (c.type === "verb" || c.type === "connector" || c.type === "preposition") return false;
          if (c.metadata?.staticDeck) return false;
          if (isWritingV2Flashcard(c)) return false;
          return true;
        } else if (studyType === "verbs") {
          return c.type === "verb" || c.metadata?.staticDeck === "verbs";
        } else if (studyType === "connectors") {
          return c.type === "connector" || c.metadata?.staticDeck === "connectors";
        } else if (studyType === "prepositions") {
          return c.type === "preposition" || c.metadata?.staticDeck === "prepositions";
        } else if (studyType === "writing_v2") {
          return isWritingV2Flashcard(c);
        }
        return true;
      });
    }

    return list;
  },

  async getFlashcardStats(filters?: { studyType?: string }): Promise<{ total: number; dueToday: number; newCards: number; reviewed: number; byRating: { again: number; hard: number; good: number; easy: number } }> {
    const studyType = filters?.studyType || "words";
    try {
      const allCards = await this.getFlashcards({ studyType });
      const now = new Date();
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      let total = allCards.length;
      let dueToday = 0;
      let newCards = 0;
      let reviewed = 0;
      const byRating = { again: 0, hard: 0, good: 0, easy: 0 };

      allCards.forEach(card => {
        if (!card.nextReviewAt || new Date(card.nextReviewAt) <= now) dueToday++;
        if (!card.reviewCount || card.reviewCount === 0) newCards++;
        
        const lastReviewed = card.lastReviewedAt ? new Date(card.lastReviewedAt) : null;
        if (lastReviewed && lastReviewed >= todayStart) {
          reviewed++;
        }
        
        // Count current status based on last rating
        if (card.metadata?.reviewHistory && Array.isArray(card.metadata.reviewHistory) && card.metadata.reviewHistory.length > 0) {
          const lastReview = card.metadata.reviewHistory[card.metadata.reviewHistory.length - 1];
          if (lastReview?.rating === "again") byRating.again++;
          else if (lastReview?.rating === "hard") byRating.hard++;
          else if (lastReview?.rating === "good") byRating.good++;
          else if (lastReview?.rating === "easy") byRating.easy++;
        }
      });

      return { total, dueToday, newCards, reviewed, byRating };
    } catch (err: any) {
      console.error("[FlashcardService] getFlashcardStats error", err);
      return { total: 0, dueToday: 0, newCards: 0, reviewed: 0, byRating: { again: 0, hard: 0, good: 0, easy: 0 } };
    }
  },

  async reviewFlashcard(cardId: string, rating: "again" | "hard" | "good" | "easy"): Promise<void> {
    try {
      let card: any;
      let isLocal = false;

      if (!isSupabaseConfigured || !supabase) {
        isLocal = true;
        const localCard = this.getLocalFlashcards().find(c => c.id === cardId);
        if (!localCard) throw new Error("Card não encontrado.");
        card = {
          ease: Number((localCard as any).ease) || 250,
          repetitions: localCard.reviewCount || 0,
          next_review_at: localCard.nextReviewAt,
          metadata: localCard.metadata || {}
        };
      } else {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) throw new Error("Usuário não autenticado.");
        
        const { data, error } = await supabase
          .from("flashcards")
          .select("ease, repetitions, next_review_at, metadata")
          .eq("id", cardId)
          .eq("user_id", userData.user.id)
          .single();
          
        if (error || !data) throw new Error("Falha ao buscar flashcard para revisão.");
        card = data;
      }

      // 1. Determine existing SRS values with fallbacks for legacy cards
      const currentEase = Number(card.ease) || 250;
      const currentRepetitions = Number(card.repetitions) || 0;
      const currentEaseFactor = card.metadata?.ease_factor ?? (currentEase / 100);
      const currentInterval = estimateExistingInterval({
        repetitions: currentRepetitions,
        nextReviewAt: card.next_review_at,
        metadata: card.metadata
      });

      const now = new Date();

      // 2. Calculate new SRS values
      const updatedSrs = updateCardSRS(
        {
          interval: currentInterval,
          ease_factor: currentEaseFactor,
          repetitions: currentRepetitions,
          next_review_date: card.next_review_at ? new Date(card.next_review_at) : now
        },
        rating,
        now
      );

      // 3. Keep review history log
      const reviewLog = {
        reviewedAt: now.toISOString(),
        rating,
        previousEase: currentEase,
        newEase: Math.round(updatedSrs.ease_factor * 100),
        previousRepetitions: currentRepetitions,
        newRepetitions: updatedSrs.repetitions,
        previousNextReviewAt: card.next_review_at,
        newNextReviewAt: updatedSrs.next_review_date.toISOString(),
        previousInterval: currentInterval,
        newInterval: updatedSrs.interval
      };

      const newMetadata = {
        ...(card.metadata || {}),
        interval: updatedSrs.interval,
        ease_factor: updatedSrs.ease_factor,
        repetitions: updatedSrs.repetitions,
        next_review_date: updatedSrs.next_review_date.toISOString()
      };
      
      const history = Array.isArray(newMetadata.reviewHistory) ? newMetadata.reviewHistory : [];
      newMetadata.reviewHistory = [...history, reviewLog].slice(-100);

      const updatePayload = {
        ease: Math.round(updatedSrs.ease_factor * 100),
        repetitions: updatedSrs.repetitions,
        last_reviewed_at: now.toISOString(),
        next_review_at: updatedSrs.next_review_date.toISOString(),
        metadata: newMetadata,
        updated_at: now.toISOString()
      };

      if (isLocal) {
        this.updateLocalFlashcard(cardId, {
          ease: updatePayload.ease,
          reviewCount: updatePayload.repetitions,
          nextReviewAt: updatePayload.next_review_at,
          lastReviewedAt: updatePayload.last_reviewed_at,
          metadata: updatePayload.metadata,
          updatedAt: updatePayload.updated_at
        } as any);
      } else {
        const { error: updateError } = await supabase
          .from("flashcards")
          .update(updatePayload)
          .eq("id", cardId);
        if (updateError) throw updateError;
      }

      this.emitUpdates();
    } catch (err: any) {
      console.error("[FlashcardService] review failed", err);
      throw err;
    }
  },

  // --- LOCAL FALLBACK METHODS ---
  getLocalFlashcards(): FlashcardRecord[] {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flashcardsData));
      return flashcardsData as FlashcardRecord[];
    }
    try {
      return JSON.parse(saved);
    } catch {
      return flashcardsData as FlashcardRecord[];
    }
  },

  createLocalFlashcard(card: Omit<FlashcardRecord, "id"> & { id?: string }): FlashcardRecord {
    const list = this.getLocalFlashcards();
    const newItem: FlashcardRecord = {
      ...card,
      id: card.id || "fc_" + Date.now() + "_" + Math.random().toString(36).slice(2, 5),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      nextReviewAt: card.nextReviewAt || new Date().toISOString(),
      reviewCount: card.reviewCount || 0
    };
    list.push(newItem);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    this.emitUpdates();
    return newItem;
  },

  updateLocalFlashcard(id: string, partial: Partial<FlashcardRecord>): FlashcardRecord {
    const list = this.getLocalFlashcards();
    const idx = list.findIndex(c => c.id === id);
    if (idx === -1) {
      throw new Error(`Local flashcard ID ${id} not found.`);
    }
    const updated = { ...list[idx], ...partial };
    list[idx] = updated;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    this.emitUpdates();
    return updated;
  },

  deleteLocalFlashcard(id: string): void {
    const list = this.getLocalFlashcards();
    const filtered = list.filter(c => c.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    this.emitUpdates();
  },

  migrateStaticDecksToDatabase: async function(): Promise<number> {
    if (!isSupabaseConfigured || !supabase) return 0;
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) return 0;
      
      let count = 0;
      for (const deck of ["verbs", "connectors", "prepositions"]) {
        await this.ensureStaticDeckImported(deck as any);
        count++;
      }
      this.emitUpdates();
      return count;
    } catch (err) {
      console.error("[FlashcardService] migrate error", err);
      return 0;
    }
  }
};
